import { Grid, styled, Tab, Typography, Paper } from "@mui/material";
import { ColorTabs } from "components/ColorTabs";
import { api, types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { TopicEvaluation } from "./Evaluation/TopicEvaluation";
import { CapabilityEvaluation } from "./Evaluation/CapabilityEvaluation";
import { Comment } from "./Evaluation/Comment";
import {
	SOMETHING_WENT_WRONG,
	SUCCESS_UPDATE_CAPABILITY_GRADE,
	SUCCESS_UPDATE_TOPIC_GRADE,
	SUCCESS_ADD_COMMENT,
} from "messages";
import { ComponentProps, Loader } from "components/Loader";
import { StyledButton } from "components/StyledButton";
import { routes } from "routes";

const PREFIX = "AddStudentEvaluationPage";

const classes = {
	root: `${PREFIX}-root`,
	tabs: `${PREFIX}-tabs`,
	tabsBarBox: `${PREFIX}-tabsBarBox`,
	button: `${PREFIX}-button`,
	tabPanel: `${PREFIX}-tabPanel`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		height: "100%",
	},

	[`&.${classes.tabPanel}`]: {
		background: theme.palette.background.default,
	},
}));

interface TabPanelProps {
	children?: React.ReactNode;
	index: string;
	value: string;
}

export const AddStudentEvaluationPage = ({
	setLoading,
}: ComponentProps): JSX.Element => {
	const history = useHistory();
	const { updateLastBreadcrumbs } = useBreadcrumbs();

	const [tabVal, setTabVal] = useState("topic");

	const params = useParams<{ userId: types.UUID }>();
	const [user, setUser] = useState<types.User | null>(null);
	const [staff, setStaff] = useState<types.User | null>(null);

	const [topicGrade, setTopicGrade] = useState<types.GradeById[]>([]);
	const [capabilityGrade, setCapabilityGrade] = useState<types.GradeById[]>([]);

	const [hasTopic, setHasTopic] = useState(false);
	const [hasCapability, setHasCapability] = useState(false);

	const [err, setErr] = useState(false);
	const [submit, setSubmit] = useState(false);
	const [submitTopic, setSubmitTopic] = useState(false);
	const [submitCapability, setSubmitCapability] = useState(false);
	const [submitComment, setSubmitComment] = useState(false);

	const fetchResult = useCallback(() => {
		api
			.getGrades(params.userId, "topic")
			.then((res) => {
				if (res.status === 200) {
					setErr(false);
					const tmp = res.data.map(({ name, ...keepAttrs }) => keepAttrs);
					const grades = tmp.map(({ gradeId, grade }) => ({
						id: gradeId,
						grade: grade,
					}));
					setHasTopic(true);
					setTopicGrade(grades);
				}

				if (res.status === 204) {
					setErr(false);
				}

				api
					.getGrades(params.userId, "capability")
					.then((res2) => {
						if (res2.status === 200) {
							setErr(false);
							const tmp = res2.data.map(({ name, ...keepAttrs }) => keepAttrs);
							const grades = tmp.map(({ gradeId, grade }) => ({
								id: gradeId,
								grade: grade,
							}));
							setHasCapability(true);
							setCapabilityGrade(grades);
						}
						if (res2.status === 204) {
							setErr(false);
						}
						setLoading(false);
					})
					.catch(() => {
						setErr(true);
						setLoading(false);
					});
			})
			.catch(() => {
				setErr(true);
				setLoading(false);
			});
	}, [params.userId, setTopicGrade, setCapabilityGrade, setLoading]);

	useEffect(() => {
		if (!params.userId) {
			setErr(true);
		}

		// Get student
		api
			.getUserDetails(params.userId)
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr(false);
				setUser(res.data);
			})
			.catch(() => {
				setErr(true);
			});

		fetchResult();

		// Get staff
		api
			.getMyUserData()
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr(false);
				setStaff(res.data);
			})
			.catch(() => {
				setErr(true);
			});
	}, [params.userId, updateLastBreadcrumbs, fetchResult]);

	function updateEvaluationGrade(
		gradeType: string,
		dto: types.UpdateGrade,
	): void {
		setSubmitTopic(false);
		setSubmitCapability(false);
		setSubmitComment(false);

		if (gradeType === "topic") {
			setSubmitTopic(true);
		} else if (gradeType === "capability") {
			setSubmitCapability(true);
		}

		api
			.updateEvaluationGrade({ ...dto })
			.then((res) => {
				if (res.status !== 204) {
					setErr(true);
					return;
				}
				setErr(false);
			})
			.catch(() => {
				setErr(true);
			});
	}

	function createEvaluationGrade(
		gradeType: string,
		dto: types.UpdateGrade,
	): void {
		setSubmitTopic(false);
		setSubmitCapability(false);
		setSubmitComment(false);

		if (gradeType === "topic") {
			setSubmitTopic(true);
		} else if (gradeType === "capability") {
			setSubmitCapability(true);
		}

		api
			.createEvaluationGrade({ ...dto })
			.then((res) => {
				if (res.status !== 201) {
					setErr(true);
					return;
				}
				setErr(false);
				if (gradeType === "topic") {
					setHasTopic(true);
				} else if (gradeType === "capability") {
					setHasCapability(true);
				}
			})
			.catch(() => {
				setErr(true);
			});
	}

	// Submit update of topic
	function onSubmitTopic(event: React.FormEvent): void {
		event.preventDefault();

		if (user?.userId === undefined) {
			setErr(true);
			setSubmitTopic(true);
			return;
		}

		const dto: types.UpdateGrade = {
			studentId: params.userId,
			gradeType: "topic",
			grades: topicGrade,
		};

		if (hasTopic) {
			updateEvaluationGrade("topic", dto);
		} else {
			createEvaluationGrade("topic", dto);
		}
	}

	// Submit update of general capability
	function onSubmitCapability(event: React.FormEvent): void {
		event.preventDefault();

		if (user?.userId === undefined) {
			setErr(true);
			setSubmitCapability(true);
			return;
		}

		const dto: types.UpdateGrade = {
			studentId: params.userId,
			gradeType: "capability",
			grades: capabilityGrade,
		};

		if (hasCapability) {
			updateEvaluationGrade("capability", dto);
		} else {
			createEvaluationGrade("capability", dto);
		}
	}

	const TabPanel = (props: TabPanelProps): JSX.Element => {
		const { children, value, index, ...other } = props;

		return (
			<Root
				role="tabpanel"
				hidden={value !== index}
				id={`simple-tabpanel-${index}`}
				aria-labelledby={`simple-tab-${index}`}
				{...other}
				className={classes.tabPanel}
			>
				{value === index && children}
			</Root>
		);
	};

	const handleTabChange = (
		event: React.ChangeEvent<{}>,
		newValue: string,
	): void => {
		setTabVal(newValue);
	};

	const goBack = (): void => {
		history.push(routes._.result._.Groups.path);
	};

	return (
		<Root className={classes.root}>
			<InfoSnackBar
				isOpen={submit}
				onClose={() => setSubmit(false)}
				message={err ? SOMETHING_WENT_WRONG : ""}
				severity={err ? "error" : "success"}
			/>

			<Grid
				container
				spacing={2}
				justifyContent="space-between"
				alignItems="center"
			>
				<Grid item xs={6}>
					<Typography variant="h2" data-testid="hello">
						{user?.firstName} {user?.lastName}
					</Typography>
				</Grid>

				<Grid item xs={1}>
					<StyledButton variant="outlined" onClick={goBack}>
						Go back
					</StyledButton>
				</Grid>
			</Grid>

			<ColorTabs
				value={tabVal}
				onChange={handleTabChange}
				indicatorColor="secondary"
				className={classes.tabs}
			>
				<Tab value={"topic"} label="Topic Evaluation" wrapped />
				<Tab
					value={"capability"}
					label="General Capability Evaluation"
					wrapped
					data-testid="capability-tab"
				/>
				<Tab
					value={"comment"}
					label="Comment"
					wrapped
					data-testid="comment-tab"
				/>
			</ColorTabs>

			<TabPanel value={tabVal} index="topic">
				<Paper>
					<InfoSnackBar
						isOpen={submitTopic}
						onClose={() => setSubmitTopic(false)}
						message={err ? SOMETHING_WENT_WRONG : SUCCESS_UPDATE_TOPIC_GRADE}
						severity={err ? "error" : "success"}
					/>

					<TopicEvaluation
						userIdParam={params.userId}
						hasTopic={hasTopic}
						topicGrade={topicGrade}
						setTopicGrade={setTopicGrade}
						onSubmit={onSubmitTopic}
					/>
				</Paper>
			</TabPanel>

			<TabPanel value={tabVal} index="capability">
				<Paper>
					<InfoSnackBar
						isOpen={submitCapability}
						onClose={() => setSubmitCapability(false)}
						message={
							err ? SOMETHING_WENT_WRONG : SUCCESS_UPDATE_CAPABILITY_GRADE
						}
						severity={err ? "error" : "success"}
					/>

					<CapabilityEvaluation
						userIdParam={params.userId}
						hasCapability={hasCapability}
						capabilityGrade={capabilityGrade}
						setCapabilityGrade={setCapabilityGrade}
						onSubmit={onSubmitCapability}
					/>
				</Paper>
			</TabPanel>

			<TabPanel value={tabVal} index="comment">
				<Paper>
					<InfoSnackBar
						isOpen={submitComment}
						onClose={() => setSubmitComment(false)}
						message={err ? SOMETHING_WENT_WRONG : SUCCESS_ADD_COMMENT}
						severity={err ? "error" : "success"}
					/>

					<Comment
						studentId={params.userId}
						staffId={staff ? staff.userId : undefined}
						setSubmitComment={setSubmitComment}
					/>
				</Paper>
			</TabPanel>
		</Root>
	);
};

export const AddStudentEvaluationLoading = Loader(
	AddStudentEvaluationPage,
	"We are fetching your data",
);
